import React from 'react';
import classnames from 'shared/utils/classnames';
import htmlTitle from 'shared/utils/htmlTitle';
import { CONTENT_TYPES } from 'shared/constants/survey';
import { FormattedMessage } from 'react-intl';
import { Button, Tooltip, Divider } from 'antd';
import ContentTypeIcon from 'components/ContentTypeIcon';
import Dependencies from 'components/Dependencies';
import DragHandle from 'components/DragHandle';

import {
  PlusOutlined, MinusOutlined, DeleteFilled,
  EyeOutlined, EyeInvisibleOutlined,
} from '@ant-design/icons';

import messages from './messages';

const SurveyContentTreeNodeItem = ({ onEdit, onRemove, onToggleActive, ...rstProps }) => {
  const {
    node,
    scaffoldBlockPxWidth,
    toggleChildrenVisibility,
    connectDragPreview,
    connectDragSource,
    isDragging,
    canDrop,
    didDrop,
    path,
    treeIndex,
  } = rstProps;

  let title = '';
  let type = '';

  if (node.block) {
    type = CONTENT_TYPES.BLOCK;
    title = node.block.title;
  } else if (node.question) {
    type = node.question.settings ? node.question.settings.type : CONTENT_TYPES.QUESTION;
    const isRequired = node.question.settings && node.question.settings.required;
    title = <span dangerouslySetInnerHTML={htmlTitle(node.question.title, isRequired)} />;
  } else if (node.pageBreak) {
    type = CONTENT_TYPES.PAGE_BREAK;
  } else if (node.text) {
    type = CONTENT_TYPES.TEXT;
    title = node.text.title;
  }

  const rstRowClasses = classnames(
    'rst__row',
    isDragging && !didDrop && 'rst__rowLandingPad',
    isDragging && !didDrop && !canDrop && 'rst__rowCancelPad',
  );

  const hasChildren = (node.children && (node.children.length > 0 || typeof node.children === 'function'));

  const isActive = node.active || node.pageBreak;
  const toggleEnabledTooltip = isActive
    ? <FormattedMessage {...messages.activeTooltip} />
    : <FormattedMessage {...messages.inactiveTooltip} />;
  const CollapseIcon = node.expanded ? MinusOutlined : PlusOutlined;

  return connectDragPreview(
    <div className={`content content--${type} ${!isActive ? 'content--disabled' : ''}`}>
      {toggleChildrenVisibility && hasChildren && (
        <Button
          size="small"
          className="content__collapse"
          style={{ left: -0.5 * scaffoldBlockPxWidth }}
          icon={<CollapseIcon />}
          onClick={() => toggleChildrenVisibility({ node, path, treeIndex })}
        />
      )}
      {connectDragSource(
        <div className={rstRowClasses}>
          <DragHandle />
          {node.pageBreak && <Divider dashed className="content--PAGE-BREAK-line" />}
          {!node.pageBreak && (
            <>
              <Button
                type="link"
                className="content__edit"
                onClick={() => onEdit(node, path)}
                icon={node.question && <ContentTypeIcon type={type} />}
              >
                <span className="title">{title}</span>
              </Button>
              <Dependencies
                dependencies={node.dependencies}
                dependants={node.question?.dependencyOf}
                dependencyOperator={node.dependencyOperator}
              />
              <Tooltip key="2" title={toggleEnabledTooltip}>
                <Button
                  size="small"
                  type="link"
                  icon={isActive ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  onClick={() => onToggleActive(node, path)}
                />
              </Tooltip>
            </>
          )}
          <Button
            size="small"
            type="link"
            danger
            icon={<DeleteFilled />}
            onClick={() => onRemove(node.id, path)}
          />
        </div>,
        { dropEffect: 'copy' },
      )}
    </div>,
  );
};

export default SurveyContentTreeNodeItem;
